import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { WorkflowActionType } from '../../../data/models/workflow.model';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';
import TextInput from '../../forms/TextInput';
import { useTranslation } from 'react-i18next';
import SelectMenu from '../../forms/SelectMenu';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import WorkflowWaitStepAction from './WorkflowWaitStepAction';
import WorkflowAlertStepAction from './WorkflowAlertStepAction';
import WorkflowStepPublishEventAction from './WorkflowStepPublishEventAction';
import * as uiSlice from '../../../data/ui.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import workflowUtils, { customInput } from '../../../services/workflowUtils';
import {
  StartOnEventType,
  StartOnEventValue,
  WorkflowStepFormData,
} from '../../../data/workflow-form.model';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { isJSON } from '../../../utils/string.utils';
import { SystemEvent } from '../../../data/models/system-event.model';
import { useDebounce } from '@uidotdev/usehooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmationDialog from '../../ConfirmationDialog';
import GroupedSelectMenu, {
  SelectOptionGroup,
} from '../../forms/GroupedSelectMenu';

export interface WorkflowStepRef {
  save: () => boolean;
  resetForm: () => void;
  getFormData: () => WorkflowStepFormData;
}

interface WorkflowStepProps {
  stepData: WorkflowStepFormData;
  otherSteps: WorkflowStepFormData[];
  systemEvents: SystemEvent[];
  handleFormUpdate: () => void;
  handleDeleteStep: (stepId: string) => void;
  doesStepHaveReferences: (stepId: string) => boolean;
}

const PREFIX = 'WorkflowStep';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  divider: `${PREFIX}-divider`,
  formContainer: `${PREFIX}-formContainer`,
  startOnFormContainer: `${PREFIX}-startOnFormContainer`,
  startOnTypeSelect: `${PREFIX}-startOnTypeSelect`,
  startOnValueSelect: `${PREFIX}-startOnValueSelect`,
  startOnTextInput: `${PREFIX}-startOnTextInput`,
  startOnFormRow: `${PREFIX}-startOnFormRow`,
  addStartButton: `${PREFIX}-addStartButton`,
  actionSelect: `${PREFIX}-actionSelect`,
  stepNameContainer: `${PREFIX}-stepNameContainer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  [`& .${classes.header}`]: {
    margin: 0,
  },
  [`& .${classes.divider}`]: {
    marginBottom: theme.spacing(4),
  },
  [`& .${classes.formContainer}`]: {
    gap: theme.spacing(3),
  },
  [`& .${classes.startOnFormContainer}`]: {
    marginTop: theme.spacing(1),
    gap: theme.spacing(3),
  },
  [`& .${classes.startOnFormRow}`]: {
    gap: theme.spacing(2),
  },
  [`& .${classes.startOnTypeSelect}`]: {
    width: 140,
  },
  [`& .${classes.startOnValueSelect}`]: {
    flex: 1,
  },
  [`& .${classes.startOnTextInput}`]: {
    flex: 1,
  },
  [`& .${classes.addStartButton}`]: {
    ...theme.typography.body1,
    padding: 0,
    marginTop: 10,
    width: 75,
    height: 24,
  },
  [`& .${classes.actionSelect}`]: {
    maxWidth: 300,
  },
  [`& .${classes.stepNameContainer}`]: {
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const WorkflowStep = forwardRef(
  (
    {
      stepData,
      otherSteps,
      systemEvents,
      handleFormUpdate,
      handleDeleteStep,
      doesStepHaveReferences,
    }: WorkflowStepProps,
    ref,
  ) => {
    const { t } = useTranslation('common');
    const dispatch = useAppDispatch();
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
      useState(false);
    const [deleteStepMessage, setDeleteStepMessage] = useState('');

    useImperativeHandle(ref, () => ({ save, resetForm, getFormData }));

    const methods = useForm<WorkflowStepFormData>({
      defaultValues: workflowUtils.getWorkflowStepFormDefaults(),
    });

    const { control, formState, getValues, reset, trigger } = methods;

    const { isValid } = useFormState({ control });

    const { fields, append, remove, update } = useFieldArray({
      control,
      name: 'startOn',
    });

    useEffect(() => {
      const defaultValues = {
        ...stepData,
      };
      if (stepData.isStart && stepData.startOn.length === 0) {
        defaultValues.startOn = stepData.isStart
          ? [{ startOnType: StartOnEventType.Event, startOnValue: '' }]
          : [];
      }
      reset(defaultValues); // load default values
    }, [stepData]);

    const startOnEventTypes = Object.entries(StartOnEventType).map(
      ([key, value]) => ({ key, value }),
    );

    const actions = Object.values(WorkflowActionType).map((value) => ({
      key: t(`workflows.${value}Action`),
      value,
    }));

    const handleAddStartOnClick = () => {
      append({ startOnType: StartOnEventType.Event, startOnValue: '' });
    };

    const handleStartOnRowDelete = (index: number) => {
      remove(index);
    };

    const handleMoreOptionsClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      setIsMenuOpen(true);
    };

    const handleMenuClose = () => {
      setIsMenuOpen(false);
    };

    const handleDeleteStepClick = () => {
      setIsMenuOpen(false);
      if (stepData.tempId && doesStepHaveReferences(stepData.tempId)) {
        setDeleteStepMessage(
          t('workflows.confirmDeleteStepWithTransitionsMessage'),
        );
      } else {
        setDeleteStepMessage(t('workflows.confirmDeleteStepMessage'));
      }
      setIsConfirmationDialogOpen(true);
    };

    const handleDeleteStepConfirm = () => {
      setIsConfirmationDialogOpen(false);
      handleDeleteStep(stepData.tempId ?? '');
    };

    const save = () => {
      if (isValid) {
        return true;
      } else {
        trigger();
        dispatch(uiSlice.setErrorMessage('snackbar.workflowStepSaveFailed'));
        return false;
      }
    };

    const resetForm = () => {
      reset();
      remove();
    };

    const getFormData = () => {
      return getValues();
    };

    const validateName = (value: string) => {
      const regex = /^[!-~]+$/;

      return regex.test(value) || t(`workflows.nameError`);
    };

    const startOnFields = useWatch({ control, name: 'startOn' });
    const selectedAction = useWatch({ control, name: 'action' });
    const name = useWatch({ control, name: 'name' });
    const wait = useWatch({ control, name: 'wait' });
    const publishAlert = useWatch({ control, name: 'publishAlert' });
    const taskHuman = useWatch({ control, name: 'taskHuman' });
    const publishEvent = useWatch({ control, name: 'publishEvent' });
    const debouncedName = useDebounce(name, 500);

    useEffect(() => {
      handleFormUpdate();
    }, [
      debouncedName,
      startOnFields,
      selectedAction,
      wait,
      publishAlert,
      taskHuman,
      publishEvent,
    ]);

    // reset start on value if start on type changed
    getValues().startOn.forEach((startOn, index) => {
      if (startOn.startOnValue) {
        if (
          (startOn.startOnType === StartOnEventType.Event &&
            isJSON(startOn.startOnValue)) ||
          (startOn.startOnType === StartOnEventType.Step &&
            !isJSON(startOn.startOnValue))
        ) {
          update(index, { ...startOn, startOnValue: '' });
        }
      }
    });

    let allStartOnEventValues: Array<
      StartOnEventValue[] | SelectOptionGroup[]
    > = [];
    if (startOnFields.length > 0) {
      startOnFields.forEach((field, index) => {
        const selectedStartOnType = startOnFields[index].startOnType;
        if (selectedStartOnType === StartOnEventType.Event) {
          const startOnEventValues = systemEvents.map((systemEvent) => ({
            key: workflowUtils.getVersionedSystemEventName(
              systemEvent.name,
              systemEvent.version,
            ),
            value: workflowUtils.getVersionedSystemEventName(
              systemEvent.name,
              systemEvent.version,
            ),
          }));
          startOnEventValues.push({
            key: customInput,
            value: customInput,
          });
          startOnEventValues.sort((a, b) => {
            return a.key.localeCompare(b.key);
          });
          allStartOnEventValues.push(startOnEventValues);
        } else {
          const stepEventValues = workflowUtils.getStepEventValues(
            otherSteps,
            startOnFields,
            index,
          );
          allStartOnEventValues.push(stepEventValues);
        }
      });
    }

    return (
      <StyledBox>
        <FormProvider {...methods}>
          <Box className={classes.container}>
            <Stack direction="row" className={classes.stepNameContainer}>
              <h2 className={classes.header}>{name}</h2>
              {!stepData.isStart && (
                <IconButton
                  ref={menuButtonRef}
                  onClick={handleMoreOptionsClick}
                  size="large">
                  <MoreVertIcon />
                </IconButton>
              )}
            </Stack>
            <Divider className={classes.divider} />
            <Stack direction="column" className={classes.formContainer}>
              {!stepData.isStart && (
                <TextInput
                  name="name"
                  label={t('workflows.name')}
                  control={control}
                  inputProps={{ maxLength: 50 }}
                  required
                  rules={{
                    required: t('common.requiredField'),
                    validate: validateName,
                  }}
                  errors={formState.errors}
                />
              )}
              <TextInput
                name="description"
                label={t('workflows.description')}
                control={control}
                errors={formState.errors}
                rules={{ required: t('common.requiredField') }}
                required
                multiline
              />
              <Stack direction="column">
                <Typography>{t('workflows.startOn')}</Typography>
                {fields.length > 0 && (
                  <Stack
                    direction="column"
                    className={classes.startOnFormContainer}>
                    {fields.map((startOn, index) => (
                      <Stack
                        key={startOn.id}
                        direction="row"
                        className={classes.startOnFormRow}>
                        <SelectMenu
                          name={`startOn[${index}].startOnType`}
                          label={t('workflows.trigger')}
                          className={classes.startOnTypeSelect}
                          values={startOnEventTypes}
                          valueAttr="value"
                          labelAttr="key"
                          control={control}
                          required
                          errorMessage={
                            formState.errors.startOn?.[index]?.startOnType
                              ?.message
                          }
                          rules={{ required: t('common.requiredField') }}
                        />
                        {index < startOnFields.length &&
                          startOnFields[index].startOnType ===
                            StartOnEventType.Event && (
                            <SelectMenu
                              name={`startOn[${index}].startOnValue`}
                              label={t('workflows.select')}
                              className={classes.startOnValueSelect}
                              values={
                                index < allStartOnEventValues.length
                                  ? allStartOnEventValues[index]
                                  : []
                              }
                              valueAttr="value"
                              labelAttr="key"
                              disabledAttr="isDisabled"
                              control={control}
                              required
                              errorMessage={
                                formState.errors.startOn?.[index]?.startOnValue
                                  ?.message
                              }
                              rules={{ required: t('common.requiredField') }}
                            />
                          )}
                        {index < startOnFields.length &&
                          startOnFields[index].startOnType ===
                            StartOnEventType.Step && (
                            <GroupedSelectMenu
                              name={`startOn[${index}].startOnValue`}
                              label={t('workflows.select')}
                              className={classes.startOnValueSelect}
                              values={
                                index < allStartOnEventValues.length
                                  ? (allStartOnEventValues[
                                      index
                                    ] as SelectOptionGroup[])
                                  : []
                              }
                              valueAttr="value"
                              labelAttr="key"
                              selectedLabelAttr="selectedLabel"
                              disabledAttr="isDisabled"
                              control={control}
                              required
                              errorMessage={
                                formState.errors.startOn?.[index]?.startOnValue
                                  ?.message
                              }
                              rules={{ required: t('common.requiredField') }}
                            />
                          )}
                        {index < startOnFields.length &&
                          startOnFields[index].startOnValue === customInput && (
                            <TextInput
                              name={`startOn[${index}].startOnCustomValue`}
                              label={t('workflows.customEventName')}
                              className={classes.startOnTextInput}
                              control={control}
                              required
                              rules={{ required: t('common.requiredField') }}
                              errors={formState.errors}
                            />
                          )}
                        {!(index === 0 && stepData.isStart) && (
                          <IconButton
                            aria-label="delete"
                            onClick={() => handleStartOnRowDelete(index)}>
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}
                <Stack direction="row">
                  <Button
                    className={classes.addStartButton}
                    onClick={handleAddStartOnClick}>
                    + {t('workflows.addStart')}
                  </Button>
                </Stack>
              </Stack>
              <SelectMenu
                name="action"
                label={t('workflows.action')}
                className={classes.actionSelect}
                values={actions}
                valueAttr="value"
                labelAttr="key"
                control={control}
                required
                errorMessage={formState.errors.action?.message as string}
                rules={{ required: t('common.requiredField') }}
              />
              {selectedAction === WorkflowActionType.Wait && (
                <WorkflowWaitStepAction />
              )}
              {selectedAction === WorkflowActionType.PublishAlert && (
                <WorkflowAlertStepAction
                  action={WorkflowActionType.PublishAlert}
                />
              )}
              {selectedAction === WorkflowActionType.TaskHuman && (
                <WorkflowAlertStepAction
                  action={WorkflowActionType.TaskHuman}
                />
              )}
              {selectedAction === WorkflowActionType.PublishEvent && (
                <WorkflowStepPublishEventAction />
              )}
            </Stack>
          </Box>
        </FormProvider>
        <ConfirmationDialog
          title={t('workflows.confirmDeleteStepTitle')}
          message={deleteStepMessage}
          open={isConfirmationDialogOpen}
          positiveAction={handleDeleteStepConfirm}
          negativeAction={() => setIsConfirmationDialogOpen(false)}
        />
        <Menu
          anchorEl={menuButtonRef.current}
          open={isMenuOpen}
          onClose={handleMenuClose}>
          <MenuItem onClick={handleDeleteStepClick}>
            {t('workflows.deleteStep')}
          </MenuItem>
        </Menu>
      </StyledBox>
    );
  },
);

export default WorkflowStep;
